import {
  createTheme,
  PaletteOptions,
  ThemeOptions,
  useTheme,
} from "@mui/material";
import { TypographyOptions } from "@mui/material/styles/createTypography";

type CustomPaletteOptions = {
  main: string;
  light?: string;
  dark?: string;
  light2?: string;
  dark2?: string;
  main2?: string;
  main3?: string;
  secondaryLight?: string;
};

declare module "@mui/material/styles/createPalette" {
  interface Palette {
    surface: CustomPaletteOptions;
    accent: CustomPaletteOptions;
    icon: CustomPaletteOptions;
    outline: CustomPaletteOptions;
    menu: CustomPaletteOptions;
    textMain: CustomPaletteOptions;
    brand: CustomPaletteOptions;
    chipStatus: {
      successful: { fill: string; border: string };
      failed: { fill: string; border: string };
      failedFirstValidation: { fill: string; border: string };
      missed: { fill: string; border: string };
      nonCarriage: { fill: string; border: string };
      default: { fill: string; border: string };
      pending: { fill: string; border: string };
      pendingApproval: { fill: string; border: string };
    };
  }
  interface PaletteOptions {
    surface: CustomPaletteOptions;
    accent: CustomPaletteOptions;
    icon: CustomPaletteOptions;
    outline: CustomPaletteOptions;
    menu: CustomPaletteOptions;
    textMain: CustomPaletteOptions;
    brand: CustomPaletteOptions;
    chipStatus?: {
      successful?: { fill: string; border: string };
      failed?: { fill: string; border: string };
      failedFirstValidation?: { fill: string; border: string };
      missed?: { fill: string; border: string };
      pending?: { fill: string; border: string };
      pendingApproval?: { fill: string; border: string };
      nonCarriage?: { fill: string; border: string };
      default?: { fill: string; border: string };
    };
  }
}

interface ExtendedPaletteOptions extends PaletteOptions {
  surface: CustomPaletteOptions;
  accent: CustomPaletteOptions;
  icon: CustomPaletteOptions;
  outline: CustomPaletteOptions;
  menu: CustomPaletteOptions;
  textMain: CustomPaletteOptions;
  brand: CustomPaletteOptions;
  chipStatus: {
    successful: { fill: string; border: string };
    failed: { fill: string; border: string };
    failedFirstValidation: { fill: string; border: string };
    missed: { fill: string; border: string };
    pending: { fill: string; border: string };
    pendingApproval: { fill: string; border: string };
    nonCarriage: { fill: string; border: string };
    default: { fill: string; border: string };
  };
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    display: true;
    headlineLarge: true;
    headlineMedium: true;
    headlineSmall: true;
    titleMedium: true;
    titleSmall: true;
    bodyLarge: true;
    bodyMedium: true;
    bodySmall: true;
    labelLarge: true;
    labelMedium: true;
    labelSmall: true;
  }
}

export interface ExtendedTypographyOptions extends TypographyOptions {
  display?: React.CSSProperties;
  headlineLarge?: React.CSSProperties;
  headlineMedium?: React.CSSProperties;
  headlineSmall?: React.CSSProperties;
  titleMedium?: React.CSSProperties;
  titleSmall?: React.CSSProperties;
  bodyLarge?: React.CSSProperties;
  bodyMedium?: React.CSSProperties;
  bodySmall?: React.CSSProperties;
  labelLarge?: React.CSSProperties;
  labelMedium?: React.CSSProperties;
  labelSmall?: React.CSSProperties;
}

// Figma Design System File
// https://www.figma.com/design/LaOW3zwF9LRQeTnCOlTDpd/NALCAM-Design-system?node-id=585-9531&t=GJo1lCd1SRONPLk7-0
const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#6253A2",
      light: "#9480E8",
      dark: "#52197B",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#9997B3",
      light: "#B7B7D2",
      dark: "#564C4C",
      contrastText: "#000000DE",
    },
    error: {
      main: "#F45A45",
      light: "#FCD6D0",
      dark: "#AB0707",
    },
    warning: {
      main: "#F8955B",
      light: "#FCEAD0",
      dark: "#F4AC45",
    },
    surface: {
      main: "#F4F4F9",
      light: "#FFFFFF",
      dark: "#DED9F8",
    },
    accent: {
      main: "#7262B4",
    },
    icon: {
      main: "#9997B3", // Normal icon color from Figma
      light: "#B7B7D2", // Active icon color from Figma
      dark: "#564C4C",
      dark2: "#135502",
    },
    outline: {
      main: "#B7B7D2", // User section border from Figma
      light: "#C6C2DF",
      dark: "#9997B3",
      dark2: "#564C4C",
    },
    menu: {
      main: "#6253A2", // Drawer background from Figma
      light: "#9480E8", // Hover state
      dark: "#9480E8", // Active background from Figma
      light2: "#7262B4", // Active text from Figma
      dark2: "#DFDFDF", // Normal text from Figma
    },
    textMain: {
      main: "#E5E6E6", // Normal text from Figma
      main2: "#79797D",
      main3: "#DFDFDF",
      light: "#FFFFFF", // Active/emphasis text from Figma
      light2: "#FCFCFC",
      light3: "#F8F8F8",
      light4: "#F5F5F5",
      dark: "#99979A",
      dark2: "#261F1F",
      dark3: "#6253A2",
    },
    brand: {
      main: "#ACDD49",
      light: "#E2F4A6",
      dark: "#AB0707",
      secondaryLight: "#E7EFB8",
    },
    chipStatus: {
      successful: {
        fill: "#E2F4A6", // Brand/secondaryLight - Green fill
        border: "#ACDD49", // Brand/secondaryMain - Green border
      },
      failed: {
        fill: "#FFE7C2", // Surface/warningLight - Orange fill
        border: "#EAB766", // Surface/warningDark - Orange border
      },
      failedFirstValidation: {
        fill: "#FAD1D3", // Error/errorLight - Red fill
        border: "#F9A2A2", // Error/errorLight 2 - Red border
      },
      missed: {
        fill: "#FAD1D3", // Error/errorLight - Red fill
        border: "#F9A2A2", // Error/errorLight 2 - Red border
      },
      nonCarriage: {
        fill: "#FFE7C2", // Surface/warningLight - Orange fill
        border: "#EAB766", // Surface/warningDark - Orange border
      },
      default: {
        fill: "#E5E6E6", // Text/Main - Grey fill
        border: "#99979A", // Text/Dark - Grey border
      },
      pending: {
        fill: "#FFE7C2", // Surface/warningLight - Orange fill
        border: "#EAB766", // Surface/warningDark - Orange border
      },
      pendingApproval: {
        fill: "#FFE7C2", // Surface/warningLight - Orange fill
        border: "#EAB766", // Surface/warningDark - Orange border
      },
    },
  } as ExtendedPaletteOptions,
  typography: {
    // Typography uses rem units for better accessibility
    // Conversion: 1rem = 16px (browser default)
    // Formula: rem = px / 16
    fontFamily: [
      "Roboto",
      "-apple-system",
      "BlinkMacSystemFont",
      "Segoe UI",
      "sans-serif",
    ].join(", "),
    fontSize: 16, // Base font size for mobile scaling
    display: {
      fontWeight: "900",
      fontSize: "clamp(2.5rem, 5vw, 4rem)", // Responsive: 40px - 64px
      lineHeight: 1.2,
    },
    headlineLarge: {
      fontWeight: "bold",
      fontSize: "2rem", // 32px
      lineHeight: 1.3,
    },
    headlineMedium: {
      fontWeight: 600,
      fontSize: "clamp(1.25rem, 2.5vw, 1.5rem)", // Responsive: 20px - 24px
      lineHeight: 1.4,
    },
    headlineSmall: {
      fontWeight: 600,
      fontSize: "clamp(1.125rem, 2vw, 1.25rem)", // Responsive: 18px - 20px
      lineHeight: 1.4,
    },
    titleMedium: {
      fontWeight: "500",
      fontSize: "clamp(1.125rem, 2vw, 1.25rem)", // Responsive: 18px - 20px
      lineHeight: 1.5,
    },
    titleSmall: {
      fontWeight: 500,
      fontSize: "1rem", // 16px
      lineHeight: 1.5,
    },
    bodyLarge: {
      fontWeight: 400,
      fontSize: "1rem", // 16px
      lineHeight: 1.5,
    },
    bodyMedium: {
      fontWeight: "normal",
      fontSize: "0.9375rem", // 15px (increased from 14px for mobile)
      lineHeight: 1.5,
    },
    bodySmall: {
      fontWeight: "normal",
      fontSize: "0.875rem", // 14px (increased from 12px for mobile)
      lineHeight: 1.4,
    },
    labelLarge: {
      fontWeight: "300",
      fontSize: "1rem", // 16px
      lineHeight: 1.5,
    },
    labelMedium: {
      fontWeight: 500,
      fontSize: "0.875rem", // 14px (increased from 12px for mobile)
      lineHeight: 1.4,
      fontFamily: [
        "Roboto",
        "-apple-system",
        "BlinkMacSystemFont",
        "Segoe UI",
        "sans-serif",
      ].join(", "),
    },
    labelSmall: {
      fontWeight: 400,
      fontSize: "0.75rem", // 12px
      lineHeight: 1.4,
      fontFamily: [
        "Roboto",
        "-apple-system",
        "BlinkMacSystemFont",
        "Segoe UI",
        "sans-serif",
      ].join(", "),
    },
  } as ExtendedTypographyOptions,
  spacing: 10, // Changed from 8 to 10 (2 units = 20px per Figma)
  shape: {
    borderRadius: 8,
  },
  transitions: {
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          textTransform: "none",
          borderRadius: 8,
          minHeight: "44px", // Mobile-friendly touch target
          fontSize: "1rem", // 16px minimum for mobile readability
          padding: "10px 20px",
          [theme.breakpoints.down("sm")]: {
            minHeight: "48px", // Larger touch targets on mobile
            fontSize: "1.0625rem", // 17px on mobile for better readability
          },
        }),
        containedPrimary: {
          backgroundColor: "#6253A2",
          color: "#FFFFFF",
          "&:hover": {
            backgroundColor: "#7B6DB8",
          },
          "&:disabled": {
            backgroundColor: "#F4F4F9",
            color: "rgba(0, 0, 0, 0.26)",
          },
        },
        containedSecondary: {
          backgroundColor: "#9997B3",
          color: "#000000DE",
          "&:hover": {
            backgroundColor: "#A89FC4",
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.04)",
          },
          "&:disabled": {
            backgroundColor: "#F4F4F9",
            color: "rgba(0, 0, 0, 0.26)",
          },
        },
        outlinedPrimary: {
          borderColor: "#6253A2",
          color: "#6253A2",
          "&:hover": {
            borderColor: "#9480E8",
            backgroundColor: "rgba(148, 128, 232, 0.15)",
          },
        },
        outlinedSecondary: {
          borderColor: "#9997B3",
          color: "#9997B3",
          "&:hover": {
            borderColor: "#B7B7D2",
            backgroundColor: "rgba(148, 128, 232, 0.15)",
          },
        },
        textPrimary: {
          color: "#6253A2",
          "&:hover": {
            backgroundColor: "rgba(148, 128, 232, 0.15)",
          },
        },
        textSecondary: {
          color: "#9997B3",
          "&:hover": {
            backgroundColor: "rgba(148, 128, 232, 0.15)",
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          minWidth: "44px",
          minHeight: "44px",
          [theme.breakpoints.down("sm")]: {
            minWidth: "48px", // Larger touch targets on mobile
            minHeight: "48px",
          },
        }),
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ theme }) => ({
          "& .MuiInputBase-input": {
            fontSize: "1rem", // 16px to prevent zoom on iOS
            [theme.breakpoints.down("sm")]: {
              fontSize: "1rem", // Maintain 16px on mobile
            },
          },
        }),
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: ({ theme }) => ({
          fontSize: "1rem", // 16px to prevent zoom on iOS
          [theme.breakpoints.down("sm")]: {
            fontSize: "1rem", // Maintain 16px on mobile
          },
        }),
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: "#261F1F", // Dark text color for better readability
          fontSize: "1rem", // 16px - Global font size for MenuItem
          "&:hover": {
            backgroundColor: "rgba(98, 83, 162, 0.08)", // Light purple hover
          },
          "&.Mui-selected": {
            backgroundColor: "rgba(98, 83, 162, 0.12)", // Selected state
            "&:hover": {
              backgroundColor: "rgba(98, 83, 162, 0.16)",
            },
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: "#6253A2", // Figma drawer background
          borderRight: "none",
          fontFamily: [
            "Roboto",
            "-apple-system",
            "BlinkMacSystemFont",
            "Segoe UI",
            "sans-serif",
          ].join(", "), // Apply fontFamily to drawer
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          color: "#DFDFDF", // Default text color
          "&.Mui-selected": {
            backgroundColor: "#9480E8", // Figma active state
            color: "#FFFFFF",
            "&:hover": {
              backgroundColor: "#9480E8",
            },
            "& .MuiListItemIcon-root": {
              color: "#FFFFFF",
            },
            "& .MuiListItemText-primary": {
              color: "#FFFFFF", // Active text color
            },
          },
          "&:hover": {
            backgroundColor: "rgba(148, 128, 232, 0.3)",
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: "#DFDFDF", // Figma icon color
          minWidth: "24px",
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontFamily: [
            "Roboto",
            "-apple-system",
            "BlinkMacSystemFont",
            "Segoe UI",
            "sans-serif",
          ].join(", "),
          fontSize: "16px",
          fontWeight: 500,
          color: "#DFDFDF", // Figma text color
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: "#9480E8",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          width: "100%",
          minHeight: 640,
          maxHeight: 720,
          maxWidth: 760,
          margin: "0.75rem",
          "@media (max-width: 600px)": {
            margin: "0.5rem",
            maxHeight: "100%",
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          margin: 0,
          padding: "20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          height: "60px",
          backgroundColor: "#6253A2", // primary.dark
          color: "#FFFFFF",
          "& .MuiIconButton-root": {
            color: "#FFFFFF",
          },
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          gap: "1.25rem",
          display: "flex",
          flexDirection: "column",
          padding: "1.25rem 1.25rem 0.75rem 1.25rem",
          "@media (max-width: 600px)": {
            padding: "0.25rem",
          },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          borderTop: "1px solid #ccc",
          padding: "20px",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontSize: "13px",
        },
      },
    },
  },
} as ThemeOptions);

export default theme;

export type AppTheme = typeof theme;

export const useAppTheme = () => useTheme<AppTheme>();
